import axios from "axios";
import { useEffect, useState } from "react";
import { MdAdd, MdClear } from "react-icons/md";
import { Modal } from "react-responsive-modal";

import { CTA } from "@merchstores/shared/elements/Cta";
import { generateReactKey } from "@merchstores/shared/react/ReactKey";
import { toast } from "react-toastify";
import { IMemberListAdminHeaderProps } from ".";
import { sendEmail } from "@merchstores/shared/components/Mailer";

export const MemberListAdminHeader: React.FC<IMemberListAdminHeaderProps> = (props: IMemberListAdminHeaderProps) => {
  const { merchStore, setMerchStoreDetails } = props;

  const [adminList, setAdminList] = useState([]);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    const admins = merchStore.customerAdmins || [];
    if (admins.length) {
      setAdminList(admins);
    }
  }, [merchStore])

  const handleAdminModalClose = () => {
    setAdminModalOpen(false);
  }

  const handleAdminEmailChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;
    setAdminEmail(target.value);
  }

  const handleAdminSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const currentAdmins = merchStore.customerAdmins || [];
    if (!currentAdmins.includes(adminEmail)) {
      axios.post('/.netlify/functions/updateShopifyCustomerTagsByEmail', {
        customerEmail: adminEmail,
        tags: ["merch_store_admin"]
      })
        .then(resp => {
          if (resp.data.customer.length) {
            const updatedAdminList = adminList;
            updatedAdminList.push(adminEmail);
            setAdminList(updatedAdminList);
            const updatedMerchStore = merchStore;
            updatedMerchStore.customerAdmins = updatedAdminList;
            updateMerchStore(updatedMerchStore);
            sendAdminInviteEmail(adminEmail);
            toast.success("Admin added to MerchStore");
            handleAdminModalClose();
          } else {
            toast.error("The selected email does not have a Merchology login. Please direct them to create a login at merchology.com prior to adding them as an admin.")
          }
        })
        .catch(err => {
          toast.error("There was an error adding the admin. Please try again or contact support.")
          console.error(err)
        })
    } else {
      toast.warning("The selected email is already assigned as an admin. Please select a different email.")
    }
  }

  const sendAdminInviteEmail = (adminEmail: string) => {
    const storeName = merchStore.name? merchStore.name : "Untitled";
    const storeLogo = merchStore.storeLogo ? merchStore.storeLogo : '';
    const siteUrl = process.env.REACT_APP_ENV === "production" ? "https://merchologysolutions.com" : "https://staging.merchologysolutions.com";
    const email = {
      templateId: 'merchstore-admin-invite',
      subject: `Welcome to ${storeName}’s New MerchStore`,
      text: 'You’ve been assigned the Admin.',
      templateContent: [
        {
          name: 'storeName',
          content: storeName
        },
        {
          name: 'storeLogo',
          content: storeLogo
        },
        {
          name: 'siteUrl',
          content: siteUrl
        },
      ]
    }
    sendEmail(email, [adminEmail]);
  }

  const updateMerchStore = async (updatedMerchStore: any) => {
    axios.post("/.netlify/functions/updateMerchStore", {
      merchStoreData: updatedMerchStore
    })
      .then(() => {
        setMerchStoreDetails(updatedMerchStore);
      })
      .catch(err => {
        console.error(err);
        toast.warning("There was an error updating your merchstore, please try again or contact support.")
      });
  }

  const modalCloseIcon = (
    <div className="rounded-full bg-gray-200 text-12 absolute top-2 right-2 w-30 h-30 flex items-center justify-center"><MdClear /></div>
  );

  const headerCardStyles = `h-60 px-4 flex items-center text-sm font-medium w-full ${adminList.length === 1 ? "lg:w-admin-header-card-2/3" : "lg:w-create-order-header-card"} shadow-buttons border border-gray-300 rounded-xl p-0.5 mb-3 lg:mb-0`;

  return (
    <div className="mb-30">
      <Modal open={adminModalOpen} onClose={handleAdminModalClose} center classNames={{ modal: "shadow-confirmation-modal rounded-3xl" }} closeIcon={modalCloseIcon}>
        <div className="flex flex-col p-3 max-w-400">
          <div className="text-xl font-bold">
            Add Admin
          </div>
          <div className="my-5">
            <p>
              The new admin will be required to login with their merchology.com account.
            </p>
          </div>
          <form onSubmit={handleAdminSave}>
            <input
              type="email"
              placeholder="Email Address"
              className="w-full text-sm rounded-lg border border-gray-300 px-3 h-44 input-type-two"
              value={adminEmail}
              onChange={handleAdminEmailChange}
            ></input>
            <CTA size="standard" type="primary" stretch={true} classes="h-44 mt-5" formSubmit={true}>Add Admin</CTA>
            <CTA size="standard" type="clear-background" stretch={true} classes="h-44" onClick={handleAdminModalClose}>Cancel</CTA>
          </form>
        </div>
      </Modal>
      <div className="flex justify-between align-center items-start dropdowns-container px-4 md:px-0 mb-3">
        <div><p className="font-bold">Store Admins</p></div>
        <div className="text-xs opacity-50 italic">Maximum 3 Store Admins</div>
      </div>
      <div className={`${adminList.length === 1 ? "w-full lg:w-2/3" : "w-full"} flex flex-col lg:flex-row justify-between mt-3 px-5 md:px-0`}>
        {adminList.map((admin: string, index: number) => {
          return (
            <div key={generateReactKey(`admin-card-${index}`)} className={`${headerCardStyles}`}>
              <div className="w-full flex whitespace-nowrap overflow-x-hidden">
                <div>
                  {admin}
                </div>
              </div>
            </div>
          )
        })}
        {adminList.length < 3 && (
          <div className={`${headerCardStyles}`}>
            <div className="w-full overflow-x-hidden flex justify-between items-center opacity-50" onClick={() => setAdminModalOpen(true)}>
              <div>
                Add Admin
              </div>
              <div>
                <MdAdd />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}